import { oc } from "@orpc/contract";
import {
  SheetNameInputSchema,
  WorkbookSheetsResponseFields,
} from "./workbook-preview.contract";
import { z } from "zod";

/**
 * Read-only lookup endpoints for price lists. Used by dropdowns that pick a
 * price list to associate with a customer (ar_customers.metadata.price_list_id)
 * or as the org-level default on the Order Management module config.
 */

const PriceListOptionSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  currencyCode: z.string(),
  isActive: z.boolean(),
});

export type PriceListOption = z.infer<typeof PriceListOptionSchema>;

export const ListPriceListsResponseSchema = z.object({
  priceLists: z.array(PriceListOptionSchema),
});

export type ListPriceListsResponse = z.infer<
  typeof ListPriceListsResponseSchema
>;

export const SetCustomerPriceListInputSchema = z.object({
  customerId: z.string().uuid(),
  /** Null clears the customer's price list override. */
  priceListId: z.string().uuid().nullable(),
});

export type SetCustomerPriceListInput = z.infer<
  typeof SetCustomerPriceListInputSchema
>;

export const SetCustomerPriceListResponseSchema = z.object({
  ok: z.literal(true),
});

/**
 * Manual "temporary exception price list" overlays: a re-uploaded version of
 * a synced price list whose lines take precedence over the synced lines while
 * the overlay is active.
 */

export const PriceListOverlayCustomerSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
});

export type PriceListOverlayCustomer = z.infer<
  typeof PriceListOverlayCustomerSchema
>;

export const PriceListOverlaySchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  isActive: z.boolean(),
  createdAt: z.string(),
  createdByName: z.string().nullable(),
  effectiveFrom: z.string().nullable(),
  effectiveTo: z.string().nullable(),
  documentId: z.string().uuid().nullable(),
  totalRows: z.number(),
  matchedCount: z.number(),
  unmatchedCount: z.number(),
  skippedRows: z.number(),
  /** Customers this exception list is scoped to; empty = all customers. */
  customers: z.array(PriceListOverlayCustomerSchema),
});

export type PriceListOverlay = z.infer<typeof PriceListOverlaySchema>;

export const PriceListWithOverlaysSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  currencyCode: z.string(),
  isActive: z.boolean(),
  originSystem: z.string().nullable(),
  lineCount: z.number(),
  overlays: z.array(PriceListOverlaySchema),
});

export type PriceListWithOverlays = z.infer<typeof PriceListWithOverlaysSchema>;

export const ListPriceListsWithOverlaysResponseSchema = z.object({
  priceLists: z.array(PriceListWithOverlaysSchema),
});

export const PreviewOverlayUploadInputSchema = z.object({
  documentId: z.string().uuid(),
  sheetName: SheetNameInputSchema,
});

export const PreviewOverlayUploadResponseSchema = z.object({
  ...WorkbookSheetsResponseFields,
  headers: z.array(z.string()),
  sampleRows: z.array(z.array(z.string())),
  totalDataRows: z.number(),
  suggestedItemNoColumn: z.number().nullable(),
  suggestedPriceColumn: z.number().nullable(),
});

export type PreviewOverlayUploadResponse = z.infer<
  typeof PreviewOverlayUploadResponseSchema
>;

export const UploadOverlayInputSchema = z.object({
  parentPriceListId: z.string().uuid(),
  documentId: z.string().uuid(),
  sheetName: SheetNameInputSchema,
  itemNoColumnIndex: z.number().int().min(0),
  priceColumnIndex: z.number().int().min(0),
  name: z.string().trim().min(1).max(255).nullable().optional(),
  effectiveFrom: z.string().nullable().optional(),
  effectiveTo: z.string().nullable().optional(),
  /**
   * Scope the exception list to these customers; empty/absent = general
   * (applies to every customer of the parent list).
   */
  customerIds: z.array(z.string().uuid()).optional(),
});

export type UploadOverlayInput = z.infer<typeof UploadOverlayInputSchema>;

export const UploadOverlayResponseSchema = z.object({
  priceListId: z.string().uuid(),
  totalRows: z.number(),
  matchedCount: z.number(),
  unmatchedCount: z.number(),
  skippedRows: z.number(),
});

export type UploadOverlayResponse = z.infer<typeof UploadOverlayResponseSchema>;

export const OverlayIdInputSchema = z.object({
  overlayId: z.string().uuid(),
});

export const OverlayOkResponseSchema = z.object({
  ok: z.literal(true),
});

export const DownloadUnmatchedResponseSchema = z.object({
  fileName: z.string(),
  csv: z.string(),
});

export const RematchOverlayResponseSchema = z.object({
  healedCount: z.number(),
  remainingUnmatched: z.number(),
});

export const priceListsContracts = {
  list: oc.output(ListPriceListsResponseSchema).route({
    method: "GET",
    summary: "List active price lists",
    description:
      "Returns the organization's active base price lists (id, name, currency) for assigning to customers or as the order default. Excludes exception lists.",
  }),
  setCustomer: oc
    .input(SetCustomerPriceListInputSchema)
    .output(SetCustomerPriceListResponseSchema)
    .route({
      method: "POST",
      path: "/set-customer",
      summary: "Set a customer's price list",
      description:
        "Assigns a price list to a customer, or clears it with null so orders fall back to the organization's default price list.",
    }),
  listWithOverlays: oc.output(ListPriceListsWithOverlaysResponseSchema).route({
    method: "GET",
    path: "/with-overlays",
    summary: "List price lists with exception lists",
    description:
      "Returns all base price lists with line counts and their temporary exception price lists: status, effective dates, match counts, and scoped customers.",
  }),
  previewOverlayUpload: oc
    .input(PreviewOverlayUploadInputSchema)
    .output(PreviewOverlayUploadResponseSchema)
    .route({
      method: "POST",
      path: "/overlays/preview-upload",
      summary: "Preview exception price list spreadsheet",
      description:
        "Reads an uploaded spreadsheet sheet and returns headers, sample rows, and suggested item-number and price columns for mapping. Nothing is saved.",
    }),
  uploadOverlay: oc
    .input(UploadOverlayInputSchema)
    .output(UploadOverlayResponseSchema)
    .route({
      method: "POST",
      path: "/overlays/upload",
      summary: "Upload an exception price list",
      description:
        "Creates an inactive exception price list under a base list from an uploaded spreadsheet and column mapping, optionally customer-scoped and dated. Returns match counts.",
    }),
  activateOverlay: oc
    .input(OverlayIdInputSchema)
    .output(OverlayOkResponseSchema)
    .route({
      method: "POST",
      path: "/overlays/activate",
      summary: "Activate an exception price list",
      description:
        "Turns on an exception price list so its prices override the base list; rejected if another active list already covers the same customers or org-wide scope.",
    }),
  deactivateOverlay: oc
    .input(OverlayIdInputSchema)
    .output(OverlayOkResponseSchema)
    .route({
      method: "POST",
      path: "/overlays/deactivate",
      summary: "Deactivate an exception price list",
      description:
        "Turns off an exception price list so the base price list prices apply again.",
    }),
  downloadUnmatched: oc
    .input(OverlayIdInputSchema)
    .output(DownloadUnmatchedResponseSchema)
    .route({
      method: "GET",
      path: "/overlays/unmatched-csv",
      summary: "Download unmatched exception price list rows",
      description:
        "Returns a CSV of exception price list rows whose item numbers did not match a product, with item number, price, and sheet row.",
    }),
  rematchOverlay: oc
    .input(OverlayIdInputSchema)
    .output(RematchOverlayResponseSchema)
    .route({
      method: "POST",
      path: "/overlays/rematch",
      summary: "Rematch unmatched exception price list items",
      description:
        "Retries matching an exception price list's unmatched rows against the current product catalog by SKU. Returns healed and remaining unmatched counts.",
    }),
} as const;
